<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import DayChart from '@/components/dashboards/DayChart.vue'

const props = defineProps({
  report: { type: Object, required: true },
})
const emit = defineEmits(['openRatings'])

const { t } = useI18n()

// Validated for colour-blind readers and for contrast on the light surface
const COLOR = { blue: '#0588f0', teal: '#008573' }

const agents = computed(() => props.report.agents)
const days = computed(() => props.report.days)
const conversationPoints = computed(() =>
  days.value.map(d => ({ day: d.day, value: d.conversations }))
)
const onlinePoints = computed(() =>
  days.value.map(d => ({ day: d.day, value: d.online_h }))
)
const fmt = n => (n == null ? '—' : new Intl.NumberFormat('ru-RU').format(n))
const fmtMin = s => {
  if (s == null) return '—'
  return s < 60
    ? `${s} ${t('MONITORING.SECONDS')}`
    : `${Math.round(s / 60)} ${t('MONITORING.MINUTES')}`
}
const fmtHours = h => (h == null ? '—' : h.toFixed(1).replace('.', ','))
const fmtAvg = v => (v == null ? '—' : v.toFixed(1).replace('.', ','))
</script>

<template>
  <div class="flex flex-col gap-6">
    <section
      class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
    >
      <h2 class="m-0 text-heading-3 text-n-slate-12">
        {{ $t('DASHBOARDS.AGENTS.TABLE') }}
      </h2>
      <p v-if="!agents.length" class="text-sm text-n-slate-11">
        {{ $t('DASHBOARDS.AGENTS.NOBODY') }}
      </p>
      <table v-else class="w-full text-sm">
        <thead>
          <tr class="text-xs text-n-slate-11">
            <th class="pb-1 font-normal text-left">
              {{ $t('DASHBOARDS.AGENTS.NAME') }}
            </th>
            <th class="pb-1 font-normal text-right">
              {{ $t('DASHBOARDS.AGENTS.CONVERSATIONS') }}
            </th>
            <th class="pb-1 font-normal text-right">
              {{ $t('DASHBOARDS.AGENTS.MESSAGES') }}
            </th>
            <th class="pb-1 font-normal text-right">
              {{ $t('DASHBOARDS.AGENTS.RESOLVED') }}
            </th>
            <th class="pb-1 font-normal text-right">
              {{ $t('DASHBOARDS.AGENTS.REPLY') }}
            </th>
            <th class="pb-1 font-normal text-right">
              {{ $t('DASHBOARDS.AGENTS.ONLINE') }}
            </th>
            <th class="pb-1 font-normal text-right">
              {{ $t('DASHBOARDS.AGENTS.DAYS') }}
            </th>
            <th class="pb-1 font-normal text-right">
              {{ $t('DASHBOARDS.AGENTS.WATCHDOG') }}
            </th>
            <th class="pb-1 font-normal text-right">
              {{ $t('DASHBOARDS.AGENTS.RATINGS') }}
            </th>
            <th class="pb-1 font-normal text-right">
              {{ $t('DASHBOARDS.AGENTS.CLIENT_RATINGS') }}
            </th>
            <th class="pb-1 font-normal text-right">
              {{ $t('DASHBOARDS.AGENTS.CLIENT_AVG') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="a in agents"
            :key="a.user_id"
            class="border-t border-n-weak"
          >
            <td class="py-2 text-n-slate-12">{{ a.name }}</td>
            <td class="py-2 text-right tabular-nums text-n-slate-12">
              {{ fmt(a.conversations) }}
            </td>
            <td class="py-2 text-right tabular-nums text-n-slate-11">
              {{ fmt(a.messages) }}
            </td>
            <td class="py-2 text-right tabular-nums text-n-slate-11">
              {{ fmt(a.resolved) }}
            </td>
            <td class="py-2 text-right tabular-nums text-n-slate-12">
              {{ fmtMin(a.reply_median_s) }}
            </td>
            <td class="py-2 text-right tabular-nums text-n-slate-11">
              {{ fmtHours(a.online_h) }}
            </td>
            <td class="py-2 text-right tabular-nums text-n-slate-11">
              {{ fmt(a.days_active) }}
            </td>
            <td
              class="py-2 text-right tabular-nums"
              :class="a.watchdog ? 'text-n-amber-11' : 'text-n-slate-11'"
            >
              {{ fmt(a.watchdog) }}
            </td>
            <td class="py-2 text-right tabular-nums text-n-slate-11">
              {{ fmt(a.ratings_given) }}
            </td>
            <td class="py-2 text-right tabular-nums text-n-slate-11">
              <button
                v-if="a.client_ratings_count"
                type="button"
                class="underline decoration-dotted underline-offset-2 text-n-blue-11 hover:text-n-blue-12"
                @click="emit('openRatings', a)"
              >
                {{ fmt(a.client_ratings_count) }}
              </button>
              <span v-else>{{ fmt(a.client_ratings_count) }}</span>
            </td>
            <td class="py-2 text-right tabular-nums text-n-slate-12">
              {{ fmtAvg(a.client_ratings_avg) }}
            </td>
          </tr>
        </tbody>
      </table>
      <span class="text-xs text-n-slate-11">{{
        $t('DASHBOARDS.AGENTS.NOTE')
      }}</span>
    </section>

    <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
      <section
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <h2 class="m-0 text-heading-3 text-n-slate-12">
          {{ $t('DASHBOARDS.AGENTS.CONVERSATIONS_BY_DAY') }}
        </h2>
        <DayChart
          :points="conversationPoints"
          kind="bars"
          :color="COLOR.blue"
        />
      </section>
      <section
        class="flex flex-col gap-3 p-4 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <h2 class="m-0 text-heading-3 text-n-slate-12">
          {{ $t('DASHBOARDS.AGENTS.ONLINE_BY_DAY') }}
        </h2>
        <DayChart
          :points="onlinePoints"
          kind="bars"
          :color="COLOR.teal"
          :format="fmtHours"
        />
      </section>
    </div>
  </div>
</template>
